<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import FormInput from '@/components/Atoms/FormInput.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import ProjectTile from './ProjectTile.vue'

/**
 * Name + accent color, shared by "New workspace" and "Edit workspace" so the two
 * never drift. The live preview at the top is the same tile the sidebar will
 * show, which is what makes picking a color feel consequential rather than
 * decorative.
 */
const props = defineProps({
  modelValue: {
    type: Object,
    required: true, // { name: string, color: string }
  },
  // When editing, the workspace's own color is not "taken".
  excludeId: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'submit'])

const store = useStore()
const nameInput = ref(null)

const colors = computed(() => store.getters['Projects/projectColors'])
const projects = computed(() => store.getters['Projects/projects'])

const name = computed({
  get: () => props.modelValue.name || '',
  set: (value) => emit('update:modelValue', { ...props.modelValue, name: value }),
})
const color = computed({
  get: () => props.modelValue.color || '',
  set: (value) => emit('update:modelValue', { ...props.modelValue, color: value }),
})

// Color → workspace that already wears it, so the swatch can say *which* one.
const takenBy = computed(() => {
  const map = new Map()
  for (const project of projects.value) {
    if (project.id === props.excludeId || !project.color) continue
    if (!map.has(project.color)) map.set(project.color, project)
  }
  return map
})

const isTaken = (value) => takenBy.value.has(value)
const swatchLabel = (value) => {
  const owner = takenBy.value.get(value)
  return owner ? `Used by ${owner.name}` : `Select color ${value}`
}

const freeColors = computed(() => colors.value.filter((value) => !isTaken(value)))
const noFreeColor = computed(() => freeColors.value.length === 0)

const previewName = computed(() => name.value.trim() || 'Workspace name')

const pick = (value) => {
  if (props.disabled || (isTaken(value) && color.value !== value)) return
  color.value = value
}

defineExpose({
  focus: () => nameInput.value?.focus(),
  /** First color no other workspace uses, or the first in the palette. */
  firstFreeColor: () => freeColors.value[0] || colors.value[0] || '',
})
</script>

<template>
  <div>
    <div class="flex items-center gap-3 rounded-lg bg-surface-1 px-3 py-2.5 mb-5" aria-hidden="true">
      <ProjectTile :name="previewName" :color="color" size="size-9" textClass="text-sm" />
      <span class="min-w-0">
        <span class="block truncate text-sm font-semibold" :class="name.trim() ? 'text-white' : 'text-surface-6'">
          {{ previewName }}
        </span>
        <span class="block text-[11px] text-surface-6 leading-tight mt-0.5">How it appears in the sidebar</span>
      </span>
    </div>

    <FormInput
      ref="nameInput"
      v-model="name"
      id="project-name"
      label="Workspace name"
      placeholder="e.g. Marketing, Client X, Spring Tour 2026"
      :maxlength="60"
      :disabled="disabled"
      :is-error="isError"
      @keydown.enter.prevent="emit('submit')"
    />

    <div class="mt-5">
      <p class="block font-medium text-tiny text-surface-text-1 mb-2">Color</p>
      <div class="flex flex-wrap items-center gap-2.5" role="radiogroup" aria-label="Workspace color">
        <button
          v-for="value in colors"
          :key="value"
          type="button"
          role="radio"
          class="size-8 rounded-full grid place-items-center transition-all ring-offset-2 ring-offset-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          :class="[
            color === value ? 'ring-2 ring-white scale-105' : 'ring-0 hover:ring-2 hover:ring-surface-5',
            isTaken(value) && color !== value ? 'opacity-30 cursor-not-allowed hover:ring-0' : '',
          ]"
          :style="{ backgroundColor: value }"
          :disabled="disabled || (isTaken(value) && color !== value)"
          :aria-checked="color === value"
          :aria-label="swatchLabel(value)"
          :title="isTaken(value) && color !== value ? swatchLabel(value) : undefined"
          @click="pick(value)"
        >
          <icon-base v-if="color === value" class="text-white" :width="14" :height="14">
            <icon-checkmark />
          </icon-base>
        </button>
      </div>
      <p class="text-[11px] text-surface-6 mt-2.5 mb-0">
        <template v-if="noFreeColor && !color">
          Every color is in use. Change another workspace's color to free one up.
        </template>
        <template v-else>
          Each workspace gets its own color so you can tell them apart at a glance. Faded colors belong to other workspaces.
        </template>
      </p>
    </div>
  </div>
</template>
